// Story 8.2 (ADO-2011), AC1/AC3 — the largest quantity one basket line can hold.
//
// INVARIANT (cross-layer): lock-step with the server's `MAX_LINE_QUANTITY`
// (`libs/modules/order-capture/src/domain/cart-line.ts`, 100 000). The web does not
// import backend constants, so the value is repeated here: the quantity pickers cap
// at it so a Shopper cannot pick a value the server would refuse as a 400. A
// summed re-add past it is still refused server-side (`QUANTITY_LIMIT`), and the
// server's message is shown inline. Drift is caught by
// `apps/api/__tests__/tenancy/harness/__tests__/basket-web-mirror-lockstep.spec.ts`.
export const MAX_LINE_QUANTITY = 100_000;

/** Whether a picker at `quantity` is at the line maximum, so its Increase is disabled.
 *  The one comparison both quantity steppers use, kept here so it is unit-tested. */
export function isAtLineMaximum(quantity: number): boolean {
  return quantity >= MAX_LINE_QUANTITY;
}

/** Keep only the digits of a typed quantity, capped at the line maximum. SL1.0 parity:
 *  the listing `qty` field accepts digits only (`onkeypress="return isNumber(event);"`,
 *  `cabinet_smanager.htm:2463`; `isNumber`, `www/assets/js/shoplink.js:610-617`).
 *  An empty result is a legal in-progress draft; `typedLineQuantity` resolves it. */
export function sanitiseQuantityInput(raw: string): string {
  const digits = raw.replace(/\D/g, '');
  if (digits === '') return '';
  const typed = Number(digits);
  return typed > MAX_LINE_QUANTITY ? String(MAX_LINE_QUANTITY) : String(typed);
}

/** The quantity a typed cart-line value saves, or null when it saves nothing. Shared by
 *  the cart line row and the product card's cart stepper so their rules cannot drift: an
 *  emptied field saves nothing (the stepper has already put the quantity back), an
 *  unchanged value saves nothing, and 0 removes the line, as SL1.0's cart update drops
 *  every line below 1 (`get_cart.php:354`). `typed` is a `sanitiseQuantityInput` draft. */
export function typedLineQuantity(typed: string, current: number): number | null {
  if (typed === '') return null;
  const next = Number(typed);
  if (!Number.isInteger(next) || next === current) return null;
  return next;
}

/** SL1.0 parity: an add of 19 or more asks for confirmation first
 *  (`if (qty >= 19 ...) confirm(...)`, `cabinet_smanager.htm:399-400`). */
export const LARGE_QUANTITY_CONFIRM_THRESHOLD = 19;

export function needsLargeQuantityConfirmation(quantity: number): boolean {
  return quantity >= LARGE_QUANTITY_CONFIRM_THRESHOLD;
}

/** SL1.0's confirm wording, verbatim (`cabinet_smanager.htm:400`). */
export function largeQuantityConfirmationMessage(quantity: number): string {
  return `The qty is greater than or equal to ${LARGE_QUANTITY_CONFIRM_THRESHOLD}. Do you really wish to add ${quantity}?`;
}
